import {
  FlowStepResultTypenames,
  PinwheelExperienceInput,
  UpsertFlowStepInput,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import { DirectDepositSetupEntryPoint } from 'features/directDeposit/navigation/navigateToDirectDepositSetup';
import DirectDepositSetupIntroScreen from 'features/directDeposit/screens/DirectDepositSetupIntroScreen';
import useUpsertFlowStep from 'features/onboarding/hooks/useUpsertFlowStep';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import { useCallback } from 'react';
import useDirectDepositLoadingComponent from 'shared/hooks/pinwheel/useDirectDepositLoadingComponent/useDirectDepositLoadingComponent';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import { AbortError } from 'shared/hooks/useCancellableMutation';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export default function DirectDepositSetupPageContent() {
  const {
    entry,
    flowId,
    sourceName,
    abCohort,
    isCoinLoader: routeIsCoinLoader,
  } = useLocalSearchParams<{
    entry?: DirectDepositSetupEntryPoint;
    flowId?: string;
    sourceName?: string;
    abCohort?: string;
    isCoinLoader?: string;
  }>();

  const { isPinwheelLoading, initiatePinwheel } = usePinwheel();
  const { skipIntroScreen } = useDirectDepositLoadingComponent();
  const {
    refetchDdManagementFlow,
    fetchPinWheelFlowPageData,
    handleDdManagementLinkTokenFailure,
  } = useDirectDepositManagementFlow({
    pause: true,
  });
  const { submitUpsertFlowStep, isUpsertFlowStepLoading } = useUpsertFlowStep();

  const isCoinLoader = routeIsCoinLoader === 'true';
  const isStandaloneEntry =
    entry === 'move-money' || entry === 'direct-deposit-benefits';

  const continueOnboardingAfterPinwheel = useCallback(async () => {
    if (!flowId) {
      return;
    }

    const input: UpsertFlowStepInput = {
      id: flowId,
      intentBasedOnboarding: {
        selection: undefined,
        didSkip: false,
      },
    };

    try {
      const result = await submitUpsertFlowStep({ input });
      const upsertFlowStep = result.data?.upsertFlowStep;

      if (
        !result.error &&
        upsertFlowStep?.__typename === FlowStepResultTypenames.FlowStep
      ) {
        redirectUserToTargetRoute(upsertFlowStep);
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }

      logErrorWithContext(error, {
        featureArea: FeatureArea.DIRECT_DEPOSIT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
        severity: ErrorSeverity.LOW,
        additionalContext: {
          source:
            'DirectDepositSetupPageContent.continueOnboardingAfterPinwheel',
          flowId,
        },
      });
    }
  }, [flowId, submitUpsertFlowStep]);

  const handlePinwheelOutcome = useCallback(
    (outcome: { success: boolean }) => {
      if (isStandaloneEntry && outcome.success) {
        refetchDdManagementFlow(outcome);
        return;
      }

      if (outcome.success) {
        void fetchPinWheelFlowPageData('network-only');
        void continueOnboardingAfterPinwheel();
      }
    },
    [
      continueOnboardingAfterPinwheel,
      fetchPinWheelFlowPageData,
      isStandaloneEntry,
      refetchDdManagementFlow,
    ],
  );

  const handleInitiatePinwheel = useCallback(() => {
    initiatePinwheel({
      input: {
        experience: PinwheelExperienceInput.DirectDepositSwitch,
        skipIntroScreen,
      },
      launchBehavior: skipIntroScreen ? { delayAfterTokenMs: 3000 } : undefined,
      onOutcome: handlePinwheelOutcome,
      onLinkTokenError: handleDdManagementLinkTokenFailure,
    });
  }, [
    handlePinwheelOutcome,
    handleDdManagementLinkTokenFailure,
    initiatePinwheel,
    skipIntroScreen,
  ]);

  const isScreenLoading = isUpsertFlowStepLoading || isPinwheelLoading;

  return (
    <DirectDepositSetupIntroScreen
      onInitiatePinwheel={handleInitiatePinwheel}
      isLoadingConditions={isUpsertFlowStepLoading}
      isPinwheelLoading={isPinwheelLoading}
      isLoading={isScreenLoading}
      isCoinLoader={isCoinLoader}
      sourceName={sourceName || ''}
      abCohort={abCohort}
    />
  );
}
